import React, { useEffect, useRef, useState } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import { ApiError, apiJson } from '../../lib/api'
import { PendingButton } from '../../components/pending'
import pj from '../PickingJobsPage.module.css'
import styles from '../GeneralSettingsPage.module.css'

// D9.6 (threshold only): the label count a batch must exceed before Generate
// asks for a second confirmation. Dataphyll sets it here for the selected
// subscription; nothing configured means 2,000. It reads and saves through
// the label settings, so a save made after someone else's is refused (the
// stale guard) and every change is recorded. The server enforces it when a
// batch is generated, not just in the preview, and a change applies to
// batches generated from then on: batches already made and the identities
// issued are not touched.
const DEFAULT_THRESHOLD = 2000

interface LabelSettings {
  settings: { large_batch_threshold: number; updated_at: string | null }
}

export function LargeBatchThreshold() {
  const { user } = useAuth()
  const userRef = useRef(user)
  userRef.current = user
  const [saved, setSaved] = useState<number | null>(null)
  const [openedAt, setOpenedAt] = useState<string | null>(null)
  const [text, setText] = useState('')
  const [error, setError] = useState('')
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)

  const show = (s: LabelSettings) => {
    const n = Number(s.settings.large_batch_threshold) || DEFAULT_THRESHOLD
    setSaved(n)
    setOpenedAt(s.settings.updated_at ?? null)
    setText(String(n))
  }

  useEffect(() => {
    const u = userRef.current
    if (!u) return
    let live = true
    apiJson<LabelSettings>(u, '/api/label-printing/settings')
      .then((s) => {
        if (!live) return
        if (!s?.settings) throw new Error('no settings')
        show(s)
      })
      .catch(() => live && setError('The current threshold could not be loaded.'))
    return () => {
      live = false
    }
  }, [])

  const trimmed = text.trim()
  const parsed = /^\d+$/.test(trimmed) ? Number(trimmed) : NaN
  const valid = Number.isSafeInteger(parsed) && parsed >= 1 && parsed <= 2147483647
  const canSave = saved !== null && valid && parsed !== saved && !busy

  const save = async () => {
    const u = userRef.current
    if (!u || !canSave) return
    setBusy(true)
    setError('')
    setNote('')
    try {
      const s = await apiJson<LabelSettings>(u, '/api/label-printing/settings', {
        method: 'PUT',
        body: { large_batch_threshold: parsed, before_updated_at: openedAt },
      })
      show(s)
      setNote(`Batches above ${Number(s.settings.large_batch_threshold).toLocaleString('en-NZ')} labels now ask for confirmation.`)
    } catch (e: any) {
      const b = e instanceof ApiError ? e.body : null
      if (b?.code === 'STALE' && b.settings) {
        show(b)
        setError('Someone else changed the label settings since you opened this. This shows what is saved now.')
      } else {
        setError(e?.message || 'The threshold was not saved')
      }
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className={styles.clientField} role="listitem">
      <label className={styles.clientKey} htmlFor="cs-large-batch">
        Large label batch threshold
      </label>
      <div className={styles.clientZone}>
        <input
          id="cs-large-batch"
          type="text"
          inputMode="numeric"
          value={text}
          disabled={saved === null || busy}
          aria-invalid={trimmed !== '' && !valid ? true : undefined}
          onChange={(e) => setText(e.target.value)}
          style={{ width: '8rem' }}
        />
        <span className={styles.clientValue}>labels</span>
        <PendingButton className={canSave ? pj.save : pj.saveBlocked} disabled={!canSave} busy={busy} pending="Saving…" onClick={save}>
          Save threshold
        </PendingButton>
      </div>
      <span className={styles.clientNote}>
        A label batch above this many labels asks for a second confirmation when it is generated; at or below it does not. Whole numbers from 1; where nothing is
        set it is {DEFAULT_THRESHOLD.toLocaleString('en-NZ')}. A change applies to batches generated from then on: batches already made and the identities already
        issued are not changed.
      </span>
      {trimmed !== '' && !valid && (
        <span className={styles.clientNote} data-testid="threshold-invalid">
          Enter a whole number of labels, 1 or more.
        </span>
      )}
      {error && (
        <span className={styles.clientNote} role="alert">
          {error}
        </span>
      )}
      {note && (
        <span className={styles.clientNote} role="status">
          {note}
        </span>
      )}
    </div>
  )
}
